import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Tick = ({ id, label, checked, onChange, disabled }) => (
  <label className={`flex items-center gap-2 text-sm py-1 ${disabled ? "opacity-60" : "cursor-pointer"}`}>
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} disabled={disabled} data-testid={id} />
    <span>{label}</span>
  </label>
);

export function ExportColumnsDialog({ open, onOpenChange, kind, onExport, exporting }) {
  const [groups, setGroups] = useState([]);
  const [always, setAlways] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [sheets, setSheets] = useState({ children: true, education: true });

  useEffect(() => {
    if (!open) { return; }
    setSheets({ children: true, education: true });
    api.get("/export/columns", { params: { kind } })
      .then(({ data }) => {
        setGroups(data.groups); setAlways(data.always);
        setPicked(new Set(data.groups.flatMap((g) => g.columns)));
      })
      .catch((err) => { toast.error(formatApiError(err.response?.data?.detail)); onOpenChange(false); });
  }, [open, kind, onOpenChange]);

  const all = groups.flatMap((g) => g.columns);
  const set = (cols, on) => setPicked((prev) => {
    const next = new Set(prev);
    cols.forEach((c) => (on ? next.add(c) : next.delete(c)));
    always.forEach((c) => next.add(c));
    return next;
  });
  const submit = () => onExport({ columns: all.filter((c) => picked.has(c)).join(","), ...sheets });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="export-columns-dialog">
        <DialogHeader>
          <DialogTitle>Choose columns to export</DialogTitle>
          <DialogDescription>
            <span data-testid="export-columns-count">{picked.size} of {all.length} columns selected.</span> Membership ID is always included.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => set(all, true)} data-testid="export-columns-select-all">Select all</Button>
          <Button size="sm" variant="outline" onClick={() => set(all, false)} data-testid="export-columns-clear">Clear</Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5 mt-2">
          {groups.map((g) => {
            const on = g.columns.every((c) => picked.has(c));
            return (
              <div key={g.group} data-testid={`export-group-${slug(g.group)}`}>
                <div className="flex items-center justify-between border-b border-border pb-1 mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{g.group}</span>
                  <button type="button" className="text-xs text-primary hover:underline" onClick={() => set(g.columns, !on)}
                    data-testid={`export-group-toggle-${slug(g.group)}`}>{on ? "Clear group" : "Select group"}</button>
                </div>
                {g.columns.map((c) => (
                  <Tick key={c} id={`export-col-${slug(c)}`} label={c} checked={picked.has(c)}
                    disabled={always.includes(c)} onChange={(v) => set([c], v)} />
                ))}
              </div>
            );
          })}
          <div data-testid="export-group-sheets">
            <div className="border-b border-border pb-1 mb-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Extra sheets</span>
            </div>
            <Tick id="export-sheet-children" label="Children sheet" checked={sheets.children} onChange={(v) => setSheets((s) => ({ ...s, children: v }))} />
            <Tick id="export-sheet-education" label="Education sheet" checked={sheets.education} onChange={(v) => setSheets((s) => ({ ...s, education: v }))} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="export-columns-cancel">Cancel</Button>
          <Button onClick={submit} disabled={exporting || !groups.length} className="gap-2" data-testid="export-columns-download">
            <DownloadSimple size={17} weight="bold" /> {exporting ? "Exporting…" : "Download Excel"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
